import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import {
  InfoSection,
  PropertySectionHeader as SectionHeader,
  PropertySectionTitle as SectionTitle,
  SectionIcon,
  SectionCount,
  SectionContent,
  ExpandableList,
  ExpandableItem,
  ExpandableHeader,
  ExpandableInfo,
  ExpandableTitle,
  ExpandableSubtitle,
  ExpandIcon,
  ExpandableContent,
  ExpandableDetails,
  RestrictionStatus,
  PropertyDataRow as DataRow,
  DataValue,
  DataLabelWithTooltip,
  Icon,
} from '@popety-io/ui'
import type { RdppfWithType, LandRdppf } from '@popety-io/types'
import { rdppfService } from '@/pages/Analysis/Shared'

// Styled component for external links in extra info
const ExtraInfoLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  transition: color ${({ theme }) => theme.transitions.fast};
  word-break: break-all;

  &:hover {
    color: ${({ theme }) => theme.colors.primaryHover};
    text-decoration: underline;
  }
`

// Utility: Check if a value is a URL
const isUrl = (value: string): boolean => {
  return (
    typeof value === 'string' &&
    (value.startsWith('http://') || value.startsWith('https://'))
  )
}

// Utility: Check if a value should be displayed
const isValidValue = (value: any): boolean => {
  if (value === null || value === undefined) return false
  if (typeof value !== 'string') return true
  const trimmed = value.trim()
  return trimmed !== '' && trimmed.toLowerCase() !== 'null'
}

// Utility: Format URL for display (domain + truncated path)
const formatUrlForDisplay = (url: string): string => {
  try {
    const parsed = new URL(url)
    const path =
      parsed.pathname.length > 20
        ? parsed.pathname.slice(0, 20) + '...'
        : parsed.pathname
    return `${parsed.hostname}${path}`
  } catch {
    return url.slice(0, 40) + (url.length > 40 ? '...' : '')
  }
}

interface RestrictionsSectionProps {
  rdppfs: LandRdppf[]
  landId?: string
}

export const RestrictionsSection: React.FC<RestrictionsSectionProps> = ({
  rdppfs,
  landId,
}) => {
  const { t } = useTranslation('explore')
  const { t: tCommon } = useTranslation('common')
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())
  const [fullRdppfData, setFullRdppfData] = useState<RdppfWithType[]>([])

  // Fetch full RDPPF data with extra_information_json
  useEffect(() => {
    const fetchFullData = async () => {
      if (!landId) return
      try {
        const data = await rdppfService.getByLandId(landId)
        setFullRdppfData(data)
      } catch (error) {
        console.error('Error fetching RDPPF data:', error)
      }
    }
    fetchFullData()
  }, [landId])

  // Build display name with servitude type if available (for header display)
  const getDisplayName = (
    restriction: any,
    extraJson?: Record<string, string>
  ): string => {
    let displayName =
      restriction.name || restriction.rdppf_type?.name || 'Restriction'
    // Try to enhance name with servitude class info
    const servitudeClass = extraJson?.['Classe de la servitude']
    if (servitudeClass) {
      const servitudeType = servitudeClass.split('.')[0].trim()
      if (
        servitudeType &&
        servitudeType !== 'Null' &&
        !displayName.toLowerCase().includes(servitudeType.toLowerCase())
      ) {
        displayName = `${displayName} - ${servitudeType}`
      }
    }
    return displayName
  }

  // Render extra information dynamically from any restriction type
  const renderExtraInfo = (extraJson: Record<string, string> | undefined) => {
    if (!extraJson) return null

    const validEntries = Object.entries(extraJson).filter(([_, value]) =>
      isValidValue(value)
    )

    if (validEntries.length === 0) return null

    return validEntries.map(([key, value]) => (
      <DataRow key={key}>
        <DataLabelWithTooltip label={key}>{key}</DataLabelWithTooltip>
        <DataValue>
          {isUrl(value) ? (
            <ExtraInfoLink
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              title={value}
            >
              {formatUrlForDisplay(value)}
              <Icon name="external-link" size="small" />
            </ExtraInfoLink>
          ) : (
            value
          )}
        </DataValue>
      </DataRow>
    ))
  }

  const toggleExpand = (index: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(index)) {
        newSet.delete(index)
      } else {
        newSet.add(index)
      }
      return newSet
    })
  }

  // Merge rdppfs from Elasticsearch with full data from API
  // Use fullRdppfData if available, otherwise fall back to rdppfs
  const restrictionsToDisplay = useMemo(() => {
    if (fullRdppfData.length > 0) {
      // Use the full RDPPF data with extra_information_json
      return fullRdppfData.map((r) => {
        // Find matching basic rdppf to get cover_area_perc
        const basicRdppf = rdppfs.find((br: any) => {
          const brTypeId = br.rdppf_type_id?.toString()
          const rTypeId = r.rdppf_type_id?.toString()
          return brTypeId === rTypeId
        })
        return {
          ...r,
          cover_area_perc: basicRdppf?.cover_area_perc,
          active: r.active !== false,
        }
      })
    }
    return rdppfs
  }, [fullRdppfData, rdppfs])

  if (!restrictionsToDisplay || restrictionsToDisplay.length === 0) {
    return null
  }

  return (
    <InfoSection>
      <SectionHeader>
        <SectionTitle>
          <SectionIcon>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
          </SectionIcon>
          {t('propertyTooltip.restrictions.title')}
        </SectionTitle>
        <SectionCount>{restrictionsToDisplay.length}</SectionCount>
      </SectionHeader>
      <SectionContent>
        <ExpandableList>
          {restrictionsToDisplay.map((restriction: any, index) => {
            const restrictionId = `${index}`
            const isExpanded = expandedItems.has(restrictionId)
            const isActive = restriction.active !== false
            const extraJson = restriction.extra_information_json as
              | Record<string, string>
              | undefined
            const displayName = getDisplayName(restriction, extraJson)

            return (
              <ExpandableItem key={restrictionId} $expanded={isExpanded}>
                <ExpandableHeader onClick={() => toggleExpand(restrictionId)}>
                  <ExpandableInfo>
                    <div>
                      <ExpandableTitle>
                        {displayName}
                      </ExpandableTitle>
                      <ExpandableSubtitle>
                        <RestrictionStatus $active={isActive}>
                          {isActive
                            ? t('propertyTooltip.restrictions.active')
                            : t('propertyTooltip.restrictions.inactive')}
                        </RestrictionStatus>
                        {restriction.cover_area_perc && (
                          <span>
                            {t('propertyTooltip.restrictions.coverage', {
                              percent: (
                                restriction.cover_area_perc * 100
                              ).toFixed(0),
                            })}
                          </span>
                        )}
                      </ExpandableSubtitle>
                    </div>
                  </ExpandableInfo>
                  <ExpandIcon
                    $expanded={isExpanded}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M6 9l6 6 6-6" />
                  </ExpandIcon>
                </ExpandableHeader>
                <ExpandableContent $expanded={isExpanded}>
                  <ExpandableDetails>
                    {restriction.rdppf_type?.name && (
                      <DataRow>
                        <DataLabelWithTooltip
                          label={t('propertyTooltip.restrictions.type')}
                          explanation={tCommon(
                            'propertyExplanations.restrictions.type'
                          )}
                        >
                          {t('propertyTooltip.restrictions.type')}
                        </DataLabelWithTooltip>
                        <DataValue>
                          {restriction.rdppf_type.name}
                        </DataValue>
                      </DataRow>
                    )}
                    {restriction.cover_area_perc !== undefined && (
                      <DataRow>
                        <DataLabelWithTooltip
                          label={t('propertyTooltip.restrictions.coverageArea')}
                          explanation={tCommon(
                            'propertyExplanations.restrictions.coverageArea'
                          )}
                        >
                          {t('propertyTooltip.restrictions.coverageArea')}
                        </DataLabelWithTooltip>
                        <DataValue>
                          {(restriction.cover_area_perc * 100).toFixed(1)}%
                        </DataValue>
                      </DataRow>
                    )}
                    <DataRow>
                      <DataLabelWithTooltip
                        label={t('propertyTooltip.restrictions.status')}
                        explanation={tCommon(
                          'propertyExplanations.restrictions.status'
                        )}
                      >
                        {t('propertyTooltip.restrictions.status')}
                      </DataLabelWithTooltip>
                      <DataValue>
                        {isActive ? t('common.active') : t('common.inactive')}
                      </DataValue>
                    </DataRow>

                    {/* Dynamic extra information (handles all restriction types) */}
                    {renderExtraInfo(extraJson)}

                    {restriction.rdppf_type?.new_construction_authorized !==
                      undefined && (
                      <DataRow>
                        <DataLabelWithTooltip
                          label={t(
                            'propertyTooltip.restrictions.newConstruction'
                          )}
                          explanation={tCommon(
                            'propertyExplanations.restrictions.newConstruction'
                          )}
                        >
                          {t('propertyTooltip.restrictions.newConstruction')}
                        </DataLabelWithTooltip>
                        <DataValue>
                          {restriction.rdppf_type.new_construction_authorized
                            ? t('common.yes')
                            : t('common.no')}
                        </DataValue>
                      </DataRow>
                    )}
                    {restriction.rdppf_type?.description && (
                      <DataRow>
                        <DataLabelWithTooltip
                          label={t('propertyTooltip.restrictions.description')}
                          explanation={tCommon(
                            'propertyExplanations.restrictions.description'
                          )}
                        >
                          {t('propertyTooltip.restrictions.description')}
                        </DataLabelWithTooltip>
                        <DataValue>
                          {restriction.rdppf_type.description}
                        </DataValue>
                      </DataRow>
                    )}
                  </ExpandableDetails>
                </ExpandableContent>
              </ExpandableItem>
            )
          })}
        </ExpandableList>
      </SectionContent>
    </InfoSection>
  )
}
